/*
 * Copyright 2025 ixPilot Teknoloji A.Ş. (ixpilot2021@gmail.com)
 * All rights reserved.
 *
 * This software is proprietary and confidential.
 * Unauthorized copying, modification, or distribution of this software,
 * via any medium, is strictly prohibited.
 *
 * Developed and maintained by ixPilot Teknoloji A.Ş.
 */

// src/components/Modals/Firmalar/DegisiklikGecmisiModal.js
import React, { useState, useEffect } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Button,
  VStack,
  HStack,
  Text,
  Badge,
  Box,
  Divider,
  Spinner,
  useToast,
  Flex,
  Icon,
  Select,
  Input,
} from "@chakra-ui/react";
import {
  FiClock,
  FiUser,
  FiTruck,
  FiCreditCard,
  FiSmartphone,
  FiTag,
  FiHeadphones,
  FiMessageSquare,
  FiSearch,
} from "react-icons/fi";
import api from "utils/api";

const TIP_RENKLERI = {
  abonelik: { color: "blue", icon: FiCreditCard },
  imei: { color: "purple", icon: FiSmartphone },
  plaka: { color: "green", icon: FiTag },
  sim: { color: "orange", icon: FiSmartphone },
  destek: { color: "red", icon: FiHeadphones },
  sms: { color: "teal", icon: FiMessageSquare },
};

export default function DegisiklikGecmisiModal({ isOpen, onClose, firma }) {
  const toast = useToast();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [tipFilter, setTipFilter] = useState("");
  const [aracFilter, setAracFilter] = useState("");
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    if (isOpen && firma?.HspId) {
      loadGecmis();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, firma]);

  const loadGecmis = async () => {
    if (!firma?.HspId) return;
    setLoading(true);
    try {
      const response = await api.get(`/api/firmalar/${firma.HspId}/degisiklik-gecmisi`);
      const result = await response.json();
      setData(result);
    } catch (error) {
      toast({
        title: "Hata",
        description: "Değişiklik geçmişi yüklenemedi",
        status: "error",
        duration: 3000,
      });
    } finally {
      setLoading(false);
    }
  };

  const formatTarih = (tarihStr) => {
    if (!tarihStr) return "";
    try {
      const tarih = new Date(tarihStr);
      return tarih.toLocaleString("tr-TR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return tarihStr;
    }
  };

  const filteredKayitlar = data?.kayitlar?.filter((kayit) => {
    if (tipFilter && kayit.tip !== tipFilter) return false;
    if (aracFilter) {
      // ✅ Türkçe karakter desteği için toLocaleLowerCase kullan
      const plakaMatch = kayit.arac_plaka?.toLocaleLowerCase('tr-TR').includes(aracFilter.toLocaleLowerCase('tr-TR'));
      const imeiMatch = kayit.arac_imei?.toLowerCase().includes(aracFilter.toLowerCase());
      if (!plakaMatch && !imeiMatch) return false;
    }
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      const matches =
        kayit.baslik?.toLowerCase().includes(query) ||
        kayit.detay?.toLowerCase().includes(query) ||
        kayit.arac_plaka?.toLocaleLowerCase('tr-TR').includes(query.toLocaleLowerCase('tr-TR')) ||
        kayit.kullanici?.toLowerCase().includes(query);
      if (!matches) return false;
    }
    return true;
  }) || [];

  const uniqueTipler = [...new Set(data?.kayitlar?.map((k) => k.tip) || [])];
  // eslint-disable-next-line no-unused-vars
  const uniqueAraclar = [
    ...new Set(
      data?.kayitlar
        ?.map((k) => k.arac_plaka)
        .filter(Boolean)
        .concat(data?.kayitlar?.map((k) => k.arac_imei).filter(Boolean)) || []
    ),
  ];

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="6xl" isCentered motionPreset="slideInBottom">
      <ModalOverlay bg="blackAlpha.400" backdropFilter="blur(4px)" />
      <ModalContent borderRadius="2xl" boxShadow="xl" m={4} maxH="90vh" overflow="hidden" bg="white" p={1}>
        {/* Header */}
        <Box bg="white" p={4} borderBottom="1px solid" borderColor="gray.200">
          <ModalCloseButton size="sm" />
          <VStack spacing={1} align="start">
            <Text fontSize="lg" fontWeight="medium" color="gray.800">
              Değişiklik Geçmişi
            </Text>
            <Text fontSize="xs" color="gray.600">
              {firma?.firma_ismi || firma?.firma} - Toplam {data?.toplam_kayit || 0} kayıt
            </Text>
          </VStack>
        </Box>

        <ModalBody px={6} py={4} maxH="500px" overflowY="auto">
          {loading ? (
            <Flex justify="center" align="center" py={10}>
              <Spinner size="xl" color="blue.500" />
            </Flex>
          ) : (
            <VStack align="stretch" spacing={4}>
              {/* Filtreler */}
              <Box
                p={4}
                bg="gray.50"
                borderRadius="lg"
                border="1px solid"
                borderColor="gray.200"
              >
                <HStack spacing={3} flexWrap="wrap">
                  <Box flex={1} minW="200px">
                    <HStack spacing={2} mb={2}>
                      <Icon as={FiSearch} color="gray.500" />
                      <Text fontSize="xs" fontWeight="medium" color="gray.600">
                        Ara
                      </Text>
                    </HStack>
                    <Input
                      variant="flushed"
                      size="sm"
                      placeholder="Başlık, detay, plaka, kullanıcı..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                    />
                  </Box>
                  <Box minW="150px">
                    <Text fontSize="xs" fontWeight="medium" color="gray.600" mb={2}>
                      Tip
                    </Text>
                    <Select
                      variant="flushed"
                      size="sm"
                      value={tipFilter}
                      onChange={(e) => setTipFilter(e.target.value)}
                    >
                      <option value="">Tümü</option>
                      {uniqueTipler.map((tip) => (
                        <option key={tip} value={tip}>
                          {tip.charAt(0).toUpperCase() + tip.slice(1)}
                        </option>
                      ))}
                    </Select>
                  </Box>
                  <Box minW="150px">
                    <Text fontSize="xs" fontWeight="medium" color="gray.600" mb={2}>
                      Araç
                    </Text>
                    <Input
                      variant="flushed"
                      size="sm"
                      placeholder="Plaka veya IMEI"
                      value={aracFilter}
                      onChange={(e) => setAracFilter(e.target.value)}
                    />
                  </Box>
                  <Box alignSelf="flex-end">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => {
                        setTipFilter("");
                        setAracFilter("");
                        setSearchQuery("");
                      }}
                    >
                      Temizle
                    </Button>
                  </Box>
                </HStack>
              </Box>

              {/* Kayıtlar */}
              <Box
                maxH="60vh"
                overflowY="auto"
                border="1px solid"
                borderColor="gray.200"
                borderRadius="lg"
              >
                {filteredKayitlar.length === 0 ? (
                  <Flex justify="center" align="center" py={10}>
                    <Text color="gray.500">Kayıt bulunamadı</Text>
                  </Flex>
                ) : (
                  <VStack align="stretch" spacing={0} divider={<Divider />}>
                    {filteredKayitlar.map((kayit) => {
                      const tipInfo = TIP_RENKLERI[kayit.tip] || {
                        color: "gray",
                        icon: FiClock,
                      };
                      const TipIcon = tipInfo.icon;

                      return (
                        <Box
                          key={`${kayit.tip}-${kayit.id}`}
                          p={4}
                          _hover={{ bg: "gray.50" }}
                          transition="all 0.2s"
                        >
                          <HStack spacing={3} align="start">
                            {/* Tip Badge */}
                            <Badge
                              colorScheme={tipInfo.color}
                              px={2}
                              py={1}
                              borderRadius="md"
                              fontSize="xs"
                            >
                              <HStack spacing={1}>
                                <Icon as={TipIcon} boxSize={3} />
                                <Text>{kayit.tip.toUpperCase()}</Text>
                              </HStack>
                            </Badge>

                            {/* İçerik */}
                            <VStack align="start" spacing={1} flex={1}>
                              <HStack spacing={2} flexWrap="wrap">
                                <Text fontWeight="semibold" fontSize="sm">
                                  {kayit.baslik}
                                </Text>
                                {kayit.arac_plaka && (
                                  <Badge colorScheme="blue" fontSize="xs">
                                    <HStack spacing={1}>
                                      <Icon as={FiTruck} boxSize={2.5} />
                                      <Text>{kayit.arac_plaka}</Text>
                                    </HStack>
                                  </Badge>
                                )}
                              </HStack>
                              <Text fontSize="xs" color="gray.600">
                                {kayit.detay}
                              </Text>
                              <HStack spacing={4} fontSize="xs" color="gray.500">
                                <HStack spacing={1}>
                                  <Icon as={FiClock} boxSize={3} />
                                  <Text>{formatTarih(kayit.tarih)}</Text>
                                </HStack>
                                {kayit.kullanici && (
                                  <HStack spacing={1}>
                                    <Icon as={FiUser} boxSize={3} />
                                    <Text>{kayit.kullanici}</Text>
                                  </HStack>
                                )}
                              </HStack>
                            </VStack>
                          </HStack>
                        </Box>
                      );
                    })}
                  </VStack>
                )}
              </Box>
            </VStack>
          )}
        </ModalBody>

        <ModalFooter bg="white" px={6} py={4}>
          <Button onClick={onClose} size="sm">Kapat</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

